import Money from '@/components/text/money';
import { Badge, Box, HStack, Text, VStack } from '@chakra-ui/react';
import type { CommissionRewardReviewSummary, OrderRewardReview } from '@fluenceur/types';
import { useTranslation } from 'react-i18next';

export function CommissionRewardReviewNotice({ review }: { review?: OrderRewardReview | null }) {
  const { t } = useTranslation();
  if (!review) return null;

  return (
    <VStack align="inherit" gap={1}>
      <Badge colorPalette="orange" variant="subtle" size="sm">
        {t('commissions.rewardReview.label')}
      </Badge>
      <Text fontSize="xs" color="fg.muted">
        {t('commissions.rewardReview.firstOrderChanged')}
      </Text>
    </VStack>
  );
}

export function CommissionRewardReviewSummaryNotice({
  summaries,
  showDescription = true,
}: {
  summaries?: CommissionRewardReviewSummary[];
  showDescription?: boolean;
}) {
  const { t } = useTranslation();
  const reviews = summaries?.filter(({ commissionCount }) => commissionCount > 0) ?? [];
  if (!reviews.length) return null;
  const count = reviews.reduce((total, review) => total + review.commissionCount, 0);

  return (
    <Box
      borderWidth="1px"
      borderColor="orange.200"
      rounded="md"
      p={4}
      w="full"
      data-testid="commission-reward-review-summary"
    >
      <VStack align="stretch" gap={2}>
        <Badge colorPalette="orange" variant="subtle" alignSelf="start">
          {t('commissions.rewardReview.label')}
        </Badge>
        {showDescription ? (
          <Text fontSize="sm" color="fg.muted">
            {t('commissions.rewardReview.pendingNotice', { count })}
          </Text>
        ) : null}
        <HStack gap={2} flexWrap="wrap" fontSize="sm">
          <Text color="fg.muted">{t('commissions.rewardReview.heldAmount')}</Text>
          {reviews.map(({ amount }) => (
            <Money
              key={amount.currencyCode}
              value={amount.amount}
              currency={amount.currencyCode}
              fontSize="sm"
            />
          ))}
        </HStack>
      </VStack>
    </Box>
  );
}
